import React, { useEffect } from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { BrowserRouter as Router, Route, matchPath } from 'react-router-dom';
import { Switch } from "react-router";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import 'bootstrap/dist/css/bootstrap.css';
import 'antd/dist/reset.css';

import { Reset } from 'styled-reset'

import {
  DefaultTheme,
  Page,
} from './design';
import { PAGES } from "./appconstants";
import { BASE_URLS, axiosLoginInstance } from './api';
import PaymentLink from "./routes/paymentlink";
import SubscriptionLink from "./routes/subscriptionlink";
import Expired from "./routes/expired";
import Error from "./routes/error";
import Apple from "./routes/apple";
import PayPalApplePay from "./routes/apple/PayPalApplePay";
import BGIcon from "./assets/imgs/bg-squares.svg"
import RtpBGIcon from "./assets/imgs/RtpPaymentFeaturesBg.svg"
import AddPaymentMethod from "./routes/paymentmethod";
import InvoiceLink from "./routes/invoicelink";
import InvoicePay from "./routes/invoicelink/invoicepay";
import './assets/css/stylesheet.css'
import ErrorBoundary from "./components/errorboundary/ErrorBoundary";
import PaymentButton from "./routes/paymentbutton";
import SubscriptionButton from "./routes/subscriptionbutton";
import PaymentWidgetLink from "./routes/paymentwidgetlink";
import PaymentWidgetLinkPay from "./routes/paymentwidgetlink/widgetPay";
import PaymentPageIndex from "./routes/paymentpage";
import PaymentPagePay from "./routes/paymentpage/paymentpagepay";
import SetUpSession from "./routes/setupsession";
import PartnerLogin from "./routes/partnerlogin";
import TemporaryPassword from "./routes/temporarypassword";
import SetupPassword from "./routes/setuppassword";
import Login from "./routes/login";
import PartnerBusiness from "./routes/partnerbusiness";
import USFilingPartnerBusiness from "./routes/usfiling-partnerbusiness";
import ForgotPassword from "./routes/forgotpassword";
import ResetPassword from "./routes/resetpassword";
import Release from "./routes/releaseVersion";
import EcwidPayment from "./routes/ecwid";
import ZohoPaymentLink from './routes/zohopaymentlink';
import EcwidError from "./routes/ecwid/error";
import WooCommercePaymentError from "./routes/woocommerce/error";
import WooCommercePayment from "./routes/woocommerce";
import TransferLink from "./routes/transferlink";
import FundingsourceLink from './routes/fundingsourcelink';
import PaymentCheckout from "./routes/paymentcheckout";
import UsOnboarding from "./routes/usonboarding";
import WoocommerceOrder from './routes/woocommerceOrder';
import Events from './routes/eventDetails';
import Seats from './routes/eventDetails/seats';
import SeatsPay from './routes/eventDetails/seatsPay';
import InventoryOrder from './routes/inventoryOrder';
import UpiError from './routes/upiError';
import SubscriptionV2Link from "./routes/subscriptionv2link";
import AddPaymentMethodV2 from "./routes/paymentmethodv2";
import PlanList from './routes/paymentmethodv2/plans';
import SubscriptionButtonV2 from "./routes/subscriptionbuttonv2";
import Waiverdocs from './routes/paymentpage/components/Waiverdocs';
import TransactionCompleted from './routes/transactionCompleted';
import DonationPage from './routes/donationpage';
import DonationPagePay from './routes/donationpage/donationpagepay';
import EventPages from "./routes/eventPageDetails";
import EventPagePay from "./routes/eventPageDetails/components/eventPagePay";
import Products from "./routes/eventPageDetails/components/products";
import AccountVerification from "./routes/accountVerification";
import OnBoardDocUpload from './routes/onboardingDocUpload';
import LinkPartner from './routes/linkPartner';
import PaypalDocumentUpload from './routes/paypalDocUpload';
import RtpRegister from './routes/rtpRegister';
import RtpOtpVerification from './routes/rtpOtpVerification';
import RtpBankAccountSetup from './routes/rtpBankAccountSetup';
import RtpLogin from './routes/rtp-login';
import RtpWalletPin from './routes/rtpWalletPin';
import RtpAddedBankDetails from './routes/rtpAddedBankDetails';
import RtpPaymentLink from './routes/rtpPaymentLink';
import PlaidVerificationPage from "./routes/plaidVerification";
import useDetectMobileView from './hooks/useDetectMobileView';

const GlobalStyle = createGlobalStyle`
  body {
    position: relative;
    font-family: Open Sans;
    font-size: 14px;
    margin: 0px;
    padding: 0px;
    background-color: #F7F7F7;
    overflow-y: auto;
    overflow-x: hidden;
    background-image: url("${BGIcon}");
    background-repeat: no-repeat;
    background-attachment: fixed;
    background-position: center;
  }
`;

const BlueBgStyle = createGlobalStyle`
  body {
    position: relative;
    font-family: Open Sans;
    font-size: 14px;
    margin: 0px;
    padding: 0px;
    background-color: #F5F5FF;
    overflow-y: auto;
    overflow-x: hidden;
    background-image: url("${RtpBGIcon}");
    background-repeat: no-repeat;
    background-attachment: fixed;
    background-position: bottom left;
  }
`;

const GrayGlobalStyle = createGlobalStyle`
  body {
    font-family: Open Sans;
    font-size: 14px;
    margin: 0px;
    padding: 0px;
    overflow-y: auto;
    overflow-x: hidden;
    background-color: #F7F7F7!important;
    background-image: url("") !important;
    background-repeat: no-repeat;
    background-attachment: fixed;
    background-position: center;
  }
`;

const BGStyle = createGlobalStyle`
  body {
    position: relative;
    font-family: Open Sans;
    font-size: 14px;
    margin: 0px;
    padding: 0px;
    background-color: #F7F7F7;
    overflow-y: auto;
    overflow-x: hidden;
    background-repeat: no-repeat;
    background-attachment: fixed;
    background-position: center;
  }
`;

const HIDE_BACKGROUND_ROUTES = [
  `${PAGES.PAYMENT_WIDGET}/:paymentwidgetlinkId`, PAGES.RTP_PAYMENT_LINK, PAGES.RTP_ADDED_BANKS, PAGES.RTP_LOGIN,
  PAGES.RTP_WALLET_PIN_SETUP, PAGES.RTP_BANK_ACCOUNT_SETUP, PAGES.RTP_OTP_VERIFICATION, PAGES.RTP_REGISTER
];

const SHOW_GRAY_ROUTES = [
  `${PAGES.PAYMENTPAGE_LINK}/:id`,
];

const SHOW_BLUE_ROUTES = [
  PAGES.RTP_PAYMENT_LINK, PAGES.RTP_ADDED_BANKS, PAGES.RTP_LOGIN,
  PAGES.RTP_WALLET_PIN_SETUP, PAGES.RTP_BANK_ACCOUNT_SETUP, PAGES.RTP_OTP_VERIFICATION, PAGES.RTP_REGISTER
];

let STRIPE_SECRETE_KEY = 'pk_test_51MI8GsGVMBOtaKTKnvt8t7WqzdN3mO4XRf5YnzuErhYDChWUHEZpRoCpMNwlFlWCuLGJKBc7geshOP044hs9Qsvh00MZHDUASU';

const env = process.env;

if (env.REACT_APP_ENV === 'Production') {
  STRIPE_SECRETE_KEY = 'pk_live_51Jfzg4G89KJdJQ7JoosXQ8ezXr6ARUmQYq3QtsR2tCNNduJADrnEEB6uPqYYvHOC0cgi2iwK8z2T5V4kg0OXeS8g00KRbyjIVo';
}

const stripePromise = loadStripe(STRIPE_SECRETE_KEY);

const App = (props: Props) => {
  const { location } = props;
  // Custom Hooks To Detect Mobile View
  useDetectMobileView();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const MAINTAINACE_URL = process.env.REACT_APP_MAINTAINACE;

  const getHealthCheck = async () => {
    try {
      const { data: { entity } } = await axiosLoginInstance.get(`${BASE_URLS.MAINTAINACE_API}`);
      console.log(entity);
    } catch (e) {
      window.location = MAINTAINACE_URL;
    }
  };

  useEffect(() => {
    getHealthCheck();
  }, []);

  const showBackground = !HIDE_BACKGROUND_ROUTES.find((route) => matchPath(window.location.pathname,
    {
      path: route,
      exact: true,
      strict: false,
    }));

  const showGrayBackground = SHOW_GRAY_ROUTES.find((route) => matchPath(window.location.pathname,
    {
      path: route,
      exact: true,
      strict: false,
    }));

  const showBlueBackground = SHOW_BLUE_ROUTES.find((route) => matchPath(window.location.pathname,
    {
      path: route,
      exact: true,
      strict: false,
    }));

  return (
    <ThemeProvider theme={DefaultTheme}>
      <Reset />
      {
        showGrayBackground ? <GrayGlobalStyle /> : showBackground ? <GlobalStyle />
        : showBlueBackground ? <BlueBgStyle/> : <BGStyle />
      }
      <ErrorBoundary>
        <Elements stripe={stripePromise}>
          <Page
            style={{
              display: showBackground || showBlueBackground ? '' : 'flex',
              justifyContent: showBackground || showBlueBackground ? '' : 'center',
              alignItems: showBackground || showBlueBackground ? '' : 'center',
            }}
          >
            <Router>
              <Switch>
                <Route exact path="/release-status" component={Release} />
                <Route exact path="/waiverdocs/:id" component={Waiverdocs} />
                <Route exact path="/seat-page-link/:eventLinkId" component={Events} />
                <Route exact path="/inventory/order-link/:inventoryorderId" component={InventoryOrder} />
                <Route exact path="/seat-page-link/:eventLinkId/seats" component={Seats} />
                <Route exact path="/seat-page-link/:eventLinkId/pay" component={SeatsPay} />
                <Route exact path="/payment-page-link/:id" component={PaymentPageIndex} />
                <Route exact path="/payment-page-link/:id/pay" component={PaymentPagePay} />
                <Route exact path="/event-page/:eventPageId" component={EventPages} />
                <Route exact path="/event-page/:eventPageId" component={Products} />
                <Route exact path="/event-page-order/:eventPageOrderLinkId/pay" component={EventPagePay} />
                <Route exact path="/invoice-link/:invoiceLinkId" component={InvoiceLink} />
                <Route exact path="/invoice-link/:invoiceLinkId/pay" component={InvoicePay} />
                <Route exact path="/subscription-link/:id/payment-method/add" component={AddPaymentMethod} />
                <Route exact path="/subscription-link/:id" component={SubscriptionLink} />
                <Route path='/payment-button/paymentbutton-a8ddf0958e214bd48242be1da10bf7d8' component={() => {
                    window.location.href = 'https://secure.swirepay.com/payment-page-link/paymentpagelink-d0fc3a70712f4b01b309a1c85dfd6f4c';
                    return null;
                }}/>
                <Route exact path="/transaction-completed" component={TransactionCompleted} />
                <Route exact path="/payment-button/:paymentButtonId" component={PaymentButton} />
                <Route exact path="/payment-link/:paymentLinkId" component={PaymentLink} />
                <Route exact path="/setup-session" component={SetUpSession} />
                <Route exact path="/subscription-button/:subscriptionId" component={SubscriptionButton} />
                <Route exact path="/ecwid-order/error" component={EcwidError} />
                <Route exact path="/ecwid-order/:ecwidOrderGid" component={EcwidPayment} />
                <Route exact path="/woocommerce/error" component={WooCommercePaymentError} />
                <Route exact path="/woocommerce/:wooCommerceOrderGid" component={WooCommercePayment} />
                <Route exact path="/payment-widget-link/:paymentwidgetlinkId" component={PaymentWidgetLink} />
                <Route exact path="/widget-link-pay/:paymentwidgetlinkId/pay" component={PaymentWidgetLinkPay} />
                <Route exact path="/pay/zoho/zoho-payment-page/:zohoPaymentId" component={ZohoPaymentLink} />
                <Route exact path="/funding-source-link/:fundingSourcelinkId" component={FundingsourceLink} />
                <Route exact path="/woocommerce-order/:woocommerceOrderId" component={WoocommerceOrder} />
                <Route path="/connect" component={PartnerLogin} />
                <Route exact path="/login" component={Login} />
                <Route exact path="/temppassword" component={TemporaryPassword} />
                <Route exact path="/setuppassword" component={SetupPassword} />
                <Route exact path="/partner-business" component={PartnerBusiness} />
                <Route exact path="/us-filing-partner-business" component={USFilingPartnerBusiness} />
                <Route exact path="/forgotpassword" component={ForgotPassword} />
                <Route exact path="/resetpassword" component={ResetPassword} />
                <Route exact path="/transfer-links/:transferLinkId" component={TransferLink} />
                <Route exact path="/checkout-page/:checkoutPageId" component={PaymentCheckout} />
                <Route exact path="/subscription2-link/:id" component={SubscriptionV2Link} />
                <Route exact path="/subscription2-link/:id/payment-method/add" component={AddPaymentMethodV2} />
                <Route exact path="/subscription2-link/:id/planslist" component={PlanList} />
                <Route exact path="/subscription2button/:subscriptionId" component={SubscriptionButtonV2} />
                <Route path="/us-boarding" component={UsOnboarding} />
                <Route exact path="**/expired" component={Expired} />
                <Route exact path="**/error" component={Error} />
                <Route exact path="**/upierror" component={UpiError} />
                <Route exact path="/apple" component={Apple} />
                <Route exact path="/paypal-apple" component={PayPalApplePay} />
                <Route exact path="/donation-page-link/:id" component={DonationPage} />
                <Route exact path="/donation-page-link/:id/pay" component={DonationPagePay} />
                <Route exact path="/bank-verify/:id" component={AccountVerification} />
                <Route exact path="/kyc-document-upload" component={OnBoardDocUpload} />
                <Route exact path="/partnerlink" component={LinkPartner} />
                <Route exact path="/paypal-document-upload" component={PaypalDocumentUpload} />
                <Route exact path="/rtp-register" component={RtpRegister} />
                <Route exact path="/rtp-otp-verification" component={RtpOtpVerification} />
                <Route exact path="/rtp-bank-account-setup" component={RtpBankAccountSetup} />
                <Route exact path="/rtp-walletpin-setup" component={RtpWalletPin} />
                <Route exact path="/rtp-login" component={RtpLogin} />
                <Route exact path="/rtp-added-banks" component={RtpAddedBankDetails} />
                <Route exact path="/rtp-payment-link" component={RtpPaymentLink} />
                <Route exact path="/:referenceGid/plaid-manual-verification" component={PlaidVerificationPage} />
                <Route path="/" component={Error} />
              </Switch>
            </Router>
          </Page>
        </Elements>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
